import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  ArrowLeft, 
  Plus, 
  Search, 
  Filter, 
  FileText, 
  Calendar,

  Edit,
  Trash2,
  Eye,
  BookOpen,
  Lightbulb,
  TrendingUp
} from 'lucide-react'
import Navigation from '../components/Navigation'
import Toast from '../components/Toast'

export default function DemoResearchNotesPage() {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [filterCategory, setFilterCategory] = useState('all')
  const [activeTab, setActiveTab] = useState('all')
  const [showToast, setShowToast] = useState(false)

  // Mock研究笔记数据
  const mockNotes = [
    {
      id: 1,
      title: '趋势跟踪策略优化思路',
      content: '通过分析历史数据发现，在强势市场中，趋势跟踪策略表现最佳。建议在策略中加入动态止损机制，以提高风险控制能力。同时，可以考虑在趋势确认后再入场，减少假突破带来的损失。',
      category: 'strategy',
      tags: ['趋势跟踪', '优化', '风险控制'],
      created_at: '2024-01-15T10:30:00Z',
      updated_at: '2024-01-20T14:20:00Z',
      strategy_name: '趋势跟踪策略A',
      importance: 'high'
    },
    {
      id: 2,
      title: '市场情绪指标分析',
      content: '恐惧贪婪指数在极值区域时，往往预示着市场反转。结合RSI和MACD指标，可以更好地判断入场时机。建议在贪婪指数超过80时谨慎做多，在恐惧指数低于20时考虑抄底。',
      category: 'analysis',
      tags: ['市场情绪', '技术指标', 'RSI', 'MACD'],
      created_at: '2024-01-10T09:15:00Z',
      updated_at: '2024-01-12T16:45:00Z',
      strategy_name: '网格交易策略B',
      importance: 'medium'
    },
    {
      id: 3,
      title: '风险管理框架设计',
      content: '建立多层次风险管理体系：1. 单笔交易风险不超过总资金的2%；2. 单日最大回撤不超过5%；3. 相关性高的策略同时运行不超过3个；4. 定期评估和调整风险参数。',
      category: 'risk',
      tags: ['风险管理', '资金管理', '回撤控制'],
      created_at: '2024-01-05T11:00:00Z',
      updated_at: '2024-01-08T13:30:00Z',
      strategy_name: '均值回归策略C',
      importance: 'high'
    },
    {
      id: 4,
      title: '加密货币市场周期研究',
      content: '比特币减半周期与价格走势存在明显相关性。减半前6-12个月通常会出现上涨趋势，减半后1-2年达到峰值。建议在减半前布局，减半后逐步减仓。',
      category: 'research',
      tags: ['比特币', '减半', '周期', '市场分析'],
      created_at: '2024-01-01T08:00:00Z',
      updated_at: '2024-01-03T15:20:00Z',
      strategy_name: null,
      importance: 'medium'
    },
    {
      id: 5,
      title: 'DeFi协议风险评估',
      content: '评估DeFi协议风险时需关注：1. 智能合约审计报告；2. 团队背景和透明度；3. 流动性深度；4. 治理代币分布；5. 历史安全事件。建议分散投资，不要将所有资金投入单一协议。',
      category: 'defi',
      tags: ['DeFi', '风险评估', '智能合约', '流动性'],
      created_at: '2023-12-28T14:30:00Z',
      updated_at: '2023-12-30T10:15:00Z',
      strategy_name: null,
      importance: 'high'
    }
  ]

  const getCategoryLabel = (category: string) => {
    const categoryMap: Record<string, string> = {
      strategy: '策略研究',
      analysis: '市场分析',
      risk: '风险管理',
      research: '深度研究',
      defi: 'DeFi研究'
    }
    return categoryMap[category] || category
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'strategy':
        return 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
      case 'analysis':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      case 'risk':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      case 'research':
        return 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
      case 'defi':
        return 'bg-orange-500/20 text-orange-400 border border-orange-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getImportanceColor = (importance: string) => {
    switch (importance) {
      case 'high':
        return 'bg-red-500/20 text-red-400 border border-red-500/30'
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
      case 'low':
        return 'bg-green-500/20 text-green-400 border border-green-500/30'
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30'
    }
  }

  const getImportanceLabel = (importance: string) => {
    switch (importance) {
      case 'high':
        return '高优先级'
      case 'medium':
        return '中优先级'
      case 'low':
        return '低优先级'
      default:
        return '未知'
    }
  }

  const filteredNotes = mockNotes.filter(note => {
    const matchesSearch = note.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         note.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
    const matchesCategory = filterCategory === 'all' || note.category === filterCategory
    return matchesSearch && matchesCategory
  })

  const renderAllNotes = () => (
    <div className="space-y-6">
      {/* 搜索和筛选 */}
      <div className="card">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-ocean-secondary w-5 h-5" />
            <input
              type="text"
              placeholder="搜索笔记标题、内容或标签..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
          </div>
          <div className="flex items-center gap-3">
            <Filter className="w-5 h-5 text-ocean-secondary" />
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-violet-500"
            >
              <option value="all">所有分类</option>
              <option value="strategy">策略研究</option>
              <option value="analysis">市场分析</option>
              <option value="risk">风险管理</option>
              <option value="research">深度研究</option>
              <option value="defi">DeFi研究</option>
            </select>
          </div>
        </div>
      </div>

      {/* 笔记列表 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredNotes.map((note) => (
          <div key={note.id} className="card hover:scale-105 transition-all duration-300">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-ocean-primary mb-2 line-clamp-2">
                  {note.title}
                </h3>
                <div className="flex items-center space-x-2 mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(note.category)}`}>
                    {getCategoryLabel(note.category)}
                  </span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getImportanceColor(note.importance)}`}>
                    {getImportanceLabel(note.importance)}
                  </span>
                </div>
              </div>
            </div>

            <p className="text-sm text-ocean-secondary mb-4 line-clamp-3">
              {note.content}
            </p>

            <div className="flex flex-wrap gap-2 mb-4">
              {note.tags.map((tag, index) => (
                <span key={index} className="px-2 py-1 bg-white/10 rounded-full text-xs text-ocean-secondary">
                  #{tag}
                </span>
              ))}
            </div>

            <div className="flex items-center justify-between text-sm text-ocean-secondary mb-4">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-1" />
                {new Date(note.created_at).toLocaleDateString('zh-CN')}
              </div>
              {note.strategy_name && (
                <div className="flex items-center">
                  <TrendingUp className="w-4 h-4 mr-1" />
                  {note.strategy_name}
                </div>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <button 
                onClick={() => setShowToast(true)}
                className="flex-1 btn-secondary text-sm py-2"
              >
                <Eye className="w-4 h-4 mr-2" />
                查看详情
              </button>
              <button className="text-green-300 hover:text-green-400 p-2">
                <Edit className="w-4 h-4" />
              </button>
              <button className="text-red-300 hover:text-red-400 p-2">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredNotes.length === 0 && (
        <div className="card">
          <div className="text-center py-12">
            <FileText className="w-16 h-16 text-ocean-secondary mx-auto mb-4" />
            <h3 className="text-xl font-medium text-ocean-primary mb-2">暂无笔记</h3>
            <p className="text-ocean-secondary mb-6">
              {searchTerm || filterCategory !== 'all' 
                ? '没有找到匹配的笔记，请调整搜索条件' 
                : '开始记录您的研究发现和思考'
              }
            </p>
            {!searchTerm && filterCategory === 'all' && (
              <button className="btn-primary flex items-center mx-auto">
                <Plus className="w-4 h-4 mr-2" />
                添加笔记
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  )

  const renderTabContent = () => {
    switch (activeTab) {
      case 'all':
        return renderAllNotes()
      default:
        return renderAllNotes()
    }
  }

  return (
    <div className="min-h-screen animate-fadein">
      <Navigation />
      
      {/* 主要内容 */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* 页面头部 */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigate('/demo')}
                className="btn-secondary flex items-center justify-center"
                title="返回主页"
              >
                <ArrowLeft className="w-4 h-4 mr-2 sm:mr-2" />
                <span className="hidden sm:inline">返回主页</span>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gradient">研究笔记</h1>
                <p className="text-ocean-secondary">记录和管理您的研究发现、策略思路和市场分析</p>
              </div>
            </div>
          </div>
          <div className="flex justify-end">
            <button className="btn-primary flex items-center justify-center" title="新建笔记">
              <Plus className="w-4 h-4 mr-2 sm:mr-2" />
              <span className="hidden sm:inline">新建笔记</span>
            </button>
          </div>
        </div>

        {/* 统计卡片 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-blue-500/20 rounded-xl border border-blue-500/30">
                <FileText className="w-6 h-6 text-blue-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">总笔记数</p>
                <p className="text-2xl font-bold text-gradient">{mockNotes.length}</p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-green-500/20 rounded-xl border border-green-500/30">
                <BookOpen className="w-6 h-6 text-green-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">策略研究</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockNotes.filter(n => n.category === 'strategy').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-purple-500/20 rounded-xl border border-purple-500/30">
                <Lightbulb className="w-6 h-6 text-purple-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">高优先级</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockNotes.filter(n => n.importance === 'high').length}
                </p>
              </div>
            </div>
          </div>
          
          <div className="card">
            <div className="flex items-center">
              <div className="p-3 bg-orange-500/20 rounded-xl border border-orange-500/30">
                <TrendingUp className="w-6 h-6 text-orange-400" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-ocean-secondary">关联策略</p>
                <p className="text-2xl font-bold text-gradient">
                  {mockNotes.filter(n => n.strategy_name).length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 标签页导航 */}
        <div className="card mb-6">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('all')}
              className={`pb-2 px-1 border-b-2 font-medium transition-colors ${
                activeTab === 'all'
                  ? 'border-violet-500 text-ocean-primary'
                  : 'border-transparent text-ocean-secondary hover:text-ocean-primary'
              }`}
            >
              全部笔记
            </button>
          </div>
        </div>

        {/* 标签页内容 */}
        {renderTabContent()}
      </main>
      
      {/* Toast */}
      <Toast
        message="Demo仅做展示，详细功能请查看下方功能导航"
        isVisible={showToast}
        onClose={() => setShowToast(false)}
        duration={3000}
      />
    </div>
  )
} 